/**
 * Tasks — work given to me, and work I have handed down.
 * Route: /tasks
 * @file src/web/features/tasks/pages/index.tsx
 *
 * @llm-rule WHEN: Anyone needs to see or assign work with a deadline
 * @llm-rule NOTE: Missing a deadline raises a flag automatically. The form
 *   says so plainly, because a deadline that quietly costs you something is
 *   worse than one that warns you.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
  useConfirm,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bloomneo/uikit';
import { Plus } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import {
  TaskCard,
  TaskProgress,
  taskState,
  type TaskLike,
} from '../components/TaskCard';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';

interface Person {
  id: string;
  name: string | null;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  dueAt: string;
  status: 'PENDING' | 'DONE' | 'OVERDUE' | 'SUBMITTED';
  priority?: 'LOW' | 'NORMAL' | 'URGENT';
  startedAt?: string | null;
  completedAt: string | null;
  overdue: boolean;
  closedLate: boolean;
  delayReason: string | null;
  assignedBy: Person;
  assignedTo: Person;

  // Handed in, and the decision on it.
  submittedAt?: string | null;
  hasProof?: boolean;
  proofNote?: string | null;
  reviewedBy?: { id: string; name: string | null; orgRole: OrgRole } | null;
  reviewedAt?: string | null;
  revisionNote?: string | null;
  revisionCount?: number;
}

function due(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Default deadline: tomorrow at the end of the working day. */
function defaultDue(): string {
  const d = new Date(Date.now() + 86_400_000);
  d.setHours(18, 0, 0, 0);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}


/**
 * Defined at MODULE level on purpose.
 *
 * When this lived inside TasksPage, every keystroke re-created the function,
 * React saw a different component type, and remounted the whole subtree — so
 * the textarea was destroyed and recreated after each character and lost
 * focus. Any component rendered by another must be hoisted out of it.
 */
/**
 * Grouped list — OVERDUE, then TODAY / OPEN, then DONE, as the design does.
 * Sorting by state rather than by date puts what is on fire at the top.
 */
function TaskGroups({
  tasks,
  own,
  viewerId,
  onMarkDone,
  onStart,
  onWithdraw,
  onReview,
  onHistory,
}: {
  tasks: TaskLike[];
  own: boolean;
  /** Who is looking — the cards name the assigner and gate Withdraw on it. */
  viewerId?: string;
  onMarkDone: (task: TaskLike) => void;
  onStart: (task: TaskLike) => void;
  onWithdraw: (task: TaskLike) => void;
  onReview: (task: TaskLike) => void;
  onHistory: (task: TaskLike) => void;
}) {
  if (tasks.length === 0) {
    return (
      <p className="rounded-[14px] border border-dashed border-rule px-5 py-10 text-center text-[13px] text-ink-faint">
        {own ? 'Nothing assigned to you.' : 'Nobody below you has any work on.'}
      </p>
    );
  }

  const groups: Array<{ key: string; label: string; tone?: string; rows: TaskLike[] }> = [
    {
      key: 'OVERDUE',
      label: 'Overdue',
      tone: 'var(--dot-flag)',
      rows: tasks.filter((x) => taskState(x) === 'OVERDUE'),
    },
    /*
      IN REVIEW sits above Started: it is the state that needs someone to act,
      and the person who needs to act is the one reading this list.

      Every state taskState() can return MUST have a group here. A task whose
      state has no group is silently dropped from the list while still being
      counted by the tiles above it — which is exactly what happened when
      REVIEW was added and this array was not.
    */
    {
      key: 'REVIEW',
      label: 'In review',
      tone: 'var(--dot-offsite)',
      rows: tasks.filter((x) => taskState(x) === 'REVIEW'),
    },
    { key: 'STARTED', label: 'Started', rows: tasks.filter((x) => taskState(x) === 'STARTED') },
    { key: 'OPEN', label: 'To do', rows: tasks.filter((x) => taskState(x) === 'OPEN') },
    {
      key: 'DONE',
      label: 'Done',
      /*
        Newest completion first. Everything else on this screen is ordered by
        deadline, which is right for work still owed — but a finished list read
        by deadline puts a task closed months ago above one closed an hour ago,
        and the only question anyone asks of finished work is "what just
        landed?". Falls back to the deadline where a completion time is
        missing, e.g. rows closed before this was recorded.
      */
      rows: tasks
        .filter((x) => taskState(x) === 'DONE')
        .slice()
        .sort(
          (a, b) =>
            new Date(b.completedAt ?? b.dueAt).getTime() -
            new Date(a.completedAt ?? a.dueAt).getTime(),
        ),
    },
  ];

  // Guard the invariant above: anything the grouping missed is still shown,
  // rather than vanishing between the count and the list.
  const grouped = new Set(groups.flatMap((g) => g.rows.map((r) => r.id)));
  const ungrouped = tasks.filter((t) => !grouped.has(t.id));
  if (ungrouped.length > 0) {
    groups.push({ key: 'OTHER', label: 'Other', rows: ungrouped });
  }

  return (
    <div className="flex flex-col gap-4">
      {groups
        .filter((g) => g.rows.length > 0)
        .map((g) => (
          <div key={g.key} className="flex flex-col gap-2">
            <div className="label-mono" style={{ color: g.tone ?? 'var(--ink-faint)' }}>
              {g.label} · {g.rows.length}
            </div>
            {g.rows.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                own={own}
                viewerId={viewerId}
                onMarkDone={onMarkDone}
                onStart={onStart}
                onWithdraw={onWithdraw}
                onReview={onReview}
                onHistory={onHistory}
              />
            ))}
          </div>
        ))}
    </div>
  );
}

interface TaskEvent {
  id: string;
  kind: 'ASSIGNED' | 'STARTED' | 'SUBMITTED' | 'SENT_BACK' | 'ACCEPTED' | 'CLOSED';
  note: string | null;
  actor: Person | null;
  hasProof: boolean;
  proofName: string | null;
  at: string;
}

/** How each step of the trail reads, and the colour it carries. */
const EVENT_LABEL: Record<TaskEvent['kind'], string> = {
  ASSIGNED: 'Assigned',
  STARTED: 'Started',
  SUBMITTED: 'Handed in',
  SENT_BACK: 'Sent back',
  ACCEPTED: 'Accepted',
  CLOSED: 'Closed without a hand-in',
};

const EVENT_TONE: Record<TaskEvent['kind'], string> = {
  ASSIGNED: 'var(--ink-faint)',
  STARTED: 'var(--ink)',
  SUBMITTED: 'var(--dot-offsite)',
  SENT_BACK: 'var(--dot-flag)',
  ACCEPTED: 'var(--dot-appreciation)',
  CLOSED: 'var(--dot-appreciation)',
};

/** The states a person actually filters by, in the order they matter. */
/**
 * Turn a picked file into a base64 data URL small enough to store on the row.
 *
 * Screenshots come off a phone at several megabytes; the proof only has to be
 * legible, so it is drawn down to 1600px on its longest edge and re-encoded as
 * JPEG. Without this a single hand-in could carry more bytes than every other
 * field on the task put together.
 */
async function toCompressedDataUrl(file: File): Promise<string> {
  const raw = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // A file the browser cannot decode is not a screenshot.
    img.onerror = () => reject(new Error('That file is not an image'));
    img.src = raw;
  });

  const MAX = 1600;
  const scale = Math.min(1, MAX / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) return raw;
  // JPEG has no alpha, so a transparent PNG would go black without this.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/jpeg', 0.82);
}

/**
 * What the list is showing.
 *
 * PENDING is the default and is a ROLL-UP of everything still owed — not
 * started, started, and overdue. Finished work is deliberately excluded from
 * it: a list that opens with every task ever closed buries the handful that
 * still need doing, which is the only reason to open this screen.
 *
 * There is no "all": the four states below cover every task between them, so
 * an all-at-once view would only ever be noise on top of them.
 */
type TaskFilter = 'PENDING' | 'OVERDUE' | 'REVIEW' | 'STARTED' | 'OPEN' | 'DONE';

/** The one this screen opens on. */
const DEFAULT_FILTER: TaskFilter = 'PENDING';

const FILTERS: Array<{ key: TaskFilter; label: string; alert?: boolean }> = [
  { key: 'PENDING', label: 'pending' },
  { key: 'OVERDUE', label: 'overdue', alert: true },
  // Handed in and waiting on a person. Inside "pending" because it is not
  // finished, but on its own tile because it is the only state where the work
  // is waiting on the SUPERVISOR rather than the assignee.
  { key: 'REVIEW', label: 'in review' },
  { key: 'STARTED', label: 'started' },
  // "not started" rather than "to do": next to a "pending" roll-up, "to do"
  // reads like the same thing said twice.
  { key: 'OPEN', label: 'not started' },
  { key: 'DONE', label: 'finished' },
];

function matchesFilter(task: TaskLike, filter: TaskFilter): boolean {
  // Pending is everything still owed, whatever stage it has reached.
  if (filter === 'PENDING') return taskState(task) !== 'DONE';
  return taskState(task) === filter;
}

/**
 * A count that opens the list it counts.
 *
 * The number and the way in are the same control — reading "3 overdue" and then
 * hunting for those three in a longer list is the work this removes.
 */
function TaskFilterTile({
  n,
  label,
  active,
  alert,
  onClick,
}: {
  n: number;
  label: string;
  active: boolean;
  alert?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="flex min-w-[72px] flex-1 flex-col items-start rounded-[12px] px-3 py-2.5 text-left transition-all active:scale-[0.98]"
      style={{
        border: active ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
        background: active ? 'var(--paper-sunk)' : 'var(--paper)',
      }}
    >
      <span
        className="font-clock text-[20px] leading-none tabular-nums"
        style={{
          color: n === 0 ? 'var(--ink-ghost)' : alert ? 'var(--dot-flag)' : 'var(--ink)',
        }}
      >
        {n}
      </span>
      <span className="mt-1 text-[11px] leading-tight text-ink-faint">{label}</span>
    </button>
  );
}

function TasksPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();
  const confirm = useConfirm();

  // Work is handed DOWN. Nobody assigns to a Head or the Company Admin, so
  // "Given to me" would always be empty for both — showing an empty tab reads
  // as a bug rather than as a rule.
  const myRole = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;
  const receivesTasks = myRole !== 'COMPANY_ADMIN' && myRole !== 'HEAD';

  const [mine, setMine] = useState<Task[]>([]);
  const [assigned, setAssigned] = useState<Task[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** Set when closing a task that is already past its deadline. */
  const [lateTask, setLateTask] = useState<Task | null>(null);
  const [delayReason, setDelayReason] = useState('');

  /** The task being handed in, and the proof gathered for it. */
  const [handIn, setHandIn] = useState<Task | null>(null);
  const [proofImage, setProofImage] = useState<string | null>(null);
  const [proofName, setProofName] = useState('');
  const [proofNote, setProofNote] = useState('');
  const [proofBusy, setProofBusy] = useState(false);

  /** The task whose trail is open, its steps, and any step's proof. */
  const [historyTask, setHistoryTask] = useState<Task | null>(null);
  const [history, setHistory] = useState<TaskEvent[] | null>(null);
  const [openProof, setOpenProof] = useState<Record<string, string>>({});

  /** The submitted task being reviewed, its proof, and the rejection note. */
  const [reviewing, setReviewing] = useState<Task | null>(null);
  const [reviewProof, setReviewProof] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedToId: '',
    dueAt: defaultDue(),
    priority: 'NORMAL' as 'LOW' | 'NORMAL' | 'URGENT',
  });

  /*
    Assigning is an ACTION, not the first thing on the screen.

    The form used to occupy the top-left of this page, so everyone — including
    the people who only ever receive work — opened Tasks and was shown a blank
    form to fill in before they could reach their own list. It now lives behind
    the button, and the counts get the top of the screen.
  */
  const [assignOpen, setAssignOpen] = useState(false);

  /** Which list is on screen, and which state within it is being filtered to. */
  const [scope, setScope] = useState<'mine' | 'assigned'>(
    receivesTasks ? 'mine' : 'assigned',
  );
  const [filter, setFilter] = useState<TaskFilter>(DEFAULT_FILTER);

  const load = useCallback(async () => {
    const [mineResult, assignedResult, peopleResult] = await Promise.all([
      request<{ tasks: Task[] }>('/api/task/mine'),
      request<{ tasks: Task[] }>('/api/task/assigned'),
      request<{ people: Person[] }>('/api/task/assignable'),
    ]);

    if (mineResult.ok) setMine(mineResult.data.tasks);
    else toast.error(mineResult.error);

    if (assignedResult.ok) setAssigned(assignedResult.data.tasks);
    if (peopleResult.ok) setPeople(peopleResult.data.people);

    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  async function assign() {
    if (!form.title.trim() || !form.assignedToId) {
      toast.error('Pick someone and give the task a title');
      return;
    }

    setBusy(true);
    try {
      const result = await request('/api/task', {
        method: 'POST',
        body: { ...form, dueAt: new Date(form.dueAt).toISOString() },
      });
      if (!result.ok) return toast.error(result.error);

      toast.success('Task assigned');
      setForm({ title: '', description: '', assignedToId: '', dueAt: defaultDue(), priority: 'NORMAL' });
      setAssignOpen(false);
      void load();
    } finally {
      setBusy(false);
    }
  }

  /**
   * My own task: hand it in with proof. Someone else's: close it.
   *
   * The assignee never reaches DONE directly any more — the server turns their
   * submission into SUBMITTED and only a reviewer can finish it.
   */
  function markDone(task: Task) {
    if (task.assignedTo.id === user?.id) {
      setHandIn(task);
      setProofImage(null);
      setProofName('');
      setProofNote('');
      setDelayReason('');
      return;
    }
    void closeAsSupervisor(task);
  }

  async function closeAsSupervisor(task: Task, reason?: string) {
    // Past the deadline, an explanation is required before it can close.
    // Asking now is the only time the person actually remembers why.
    if (new Date(task.dueAt) < new Date() && !reason) {
      setLateTask(task);
      setDelayReason('');
      return;
    }

    const result = await request(`/api/task/${task.id}/done`, {
      method: 'POST',
      body: reason ? { delayReason: reason } : {},
    });
    if (!result.ok) return toast.error(result.error);

    toast.success(reason ? 'Marked done — logged as delayed' : 'Marked done');
    setLateTask(null);
    setDelayReason('');
    void load();
  }

  /** Attach the screenshot, shrinking it before it ever leaves the browser. */
  async function pickProof(file: File | undefined) {
    if (!file) return;
    try {
      setProofImage(await toCompressedDataUrl(file));
      setProofName(file.name);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'That file could not be read');
    }
  }

  /** Hand the work in for review. Proof is required; the server checks too. */
  async function submitHandIn() {
    if (!handIn || !proofImage) return;

    const late = new Date(handIn.dueAt) < new Date();
    if (late && !delayReason.trim()) {
      toast.error('This is past its deadline — say why before handing it in');
      return;
    }

    setProofBusy(true);
    try {
      const result = await request<{ message: string }>(`/api/task/${handIn.id}/done`, {
        method: 'POST',
        body: {
          proofImage,
          proofName,
          proofNote: proofNote.trim() || undefined,
          ...(late ? { delayReason: delayReason.trim() } : {}),
        },
      });
      if (!result.ok) return toast.error(result.error);

      toast.success(result.data.message);
      setHandIn(null);
      setProofImage(null);
      setProofNote('');
      setDelayReason('');
      void load();
    } finally {
      setProofBusy(false);
    }
  }

  /** Open someone's submission and pull the proof image down with it. */
  async function openReview(task: Task) {
    setReviewing(task);
    setReviewProof(null);
    setReviewNote('');
    const result = await request<{ proofImage: string }>(`/api/task/${task.id}/proof`);
    if (result.ok) setReviewProof(result.data.proofImage);
    else toast.error(result.error);
  }

  /** Open the full trail of one task. */
  async function openHistory(task: Task) {
    setHistoryTask(task);
    setHistory(null);
    setOpenProof({});
    const result = await request<{ events: TaskEvent[] }>(`/api/task/${task.id}/history`);
    if (result.ok) setHistory(result.data.events);
    else toast.error(result.error);
  }

  /** Pull one step's screenshot only when someone asks to see it. */
  async function loadStepProof(taskId: string, eventId: string) {
    if (openProof[eventId]) return;
    const result = await request<{ proofImage: string }>(
      `/api/task/${taskId}/history/${eventId}/proof`,
    );
    if (result.ok) setOpenProof((prev) => ({ ...prev, [eventId]: result.data.proofImage }));
    else toast.error(result.error);
  }

  /** Accept the work, or send it back with a note. The deadline never moves. */
  async function decideReview(decision: 'ACCEPT' | 'REJECT') {
    if (!reviewing) return;
    if (decision === 'REJECT' && !reviewNote.trim()) {
      toast.error('Say what needs fixing — they only see the task come back otherwise');
      return;
    }

    setReviewBusy(true);
    try {
      const result = await request<{ message: string }>(`/api/task/${reviewing.id}/review`, {
        method: 'POST',
        body: { decision, ...(decision === 'REJECT' ? { note: reviewNote.trim() } : {}) },
      });
      if (!result.ok) return toast.error(result.error);

      toast.success(result.data.message);
      setReviewing(null);
      setReviewProof(null);
      setReviewNote('');
      void load();
    } finally {
      setReviewBusy(false);
    }
  }

  /** Tell the assigner you have begun. */
  async function startTask(task: Task) {
    const result = await request(`/api/task/${task.id}/start`, { method: 'POST' });
    if (!result.ok) return toast.error(result.error);
    toast.success('Marked as started');
    void load();
  }

  async function withdraw(task: Task) {
    const ok = await confirm({
      title: `Withdraw "${task.title}"?`,
      description: `${task.assignedTo.name} will no longer see it.`,
      confirmLabel: 'Withdraw',
      tone: 'destructive',
    });
    if (!ok) return;

    const result = await request(`/api/task/${task.id}`, { method: 'DELETE' });
    if (!result.ok) return toast.error(result.error);
    toast.success('Task withdrawn');
    void load();
  }

  const openCount = useMemo(() => mine.filter((t) => t.status !== 'DONE').length, [mine]);
  const canAssign = people.length > 0;

  /** The list being looked at, and what survives the chosen filter. */
  const source = scope === 'mine' ? mine : assigned;
  const visible = useMemo(
    () => source.filter((t) => matchesFilter(t, filter)),
    [source, filter],
  );



  return (
    <AppShell
      wide
      title="Tasks"
      subtitle={
        !receivesTasks
          ? 'What everyone below you is working on, whoever set it.'
          : openCount === 0
            ? 'Nothing outstanding.'
            : `${openCount} task${openCount === 1 ? '' : 's'} waiting on you.`
      }
    >
      {loading ? (
        <Skeleton className="h-80 w-full rounded-[18px]" />
      ) : (
        <div className="flex flex-col gap-4">
          {/* Whose list. Only shown when this person genuinely has both. */}
          {receivesTasks && canAssign && (
            <div className="flex gap-1.5">
              {([
                { key: 'mine' as const, label: 'Given to me', n: mine.length },
                // Not "I assigned" any more: this is every task belonging to
                // the people below you, whoever set it.
                { key: 'assigned' as const, label: 'My people', n: assigned.length },
              ]).map((tab) => {
                const on = scope === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      setScope(tab.key);
                      // The filter belongs to the list being looked at; carrying
                      // "overdue" across can land on an empty screen that looks
                      // like the other list is empty.
                      setFilter(DEFAULT_FILTER);
                    }}
                    aria-pressed={on}
                    className="flex-1 rounded-full px-3 py-2 text-[13px] font-medium transition-colors"
                    style={{
                      border: on ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
                      background: on ? 'var(--ink)' : 'var(--paper)',
                      color: on ? 'var(--paper)' : 'var(--ink-soft)',
                    }}
                  >
                    {tab.label} · {tab.n}
                  </button>
                );
              })}
            </div>
          )}

          {/* THE COUNTS, at the top, each one a way into the list below it. */}
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
            {FILTERS.map((f) => (
              <TaskFilterTile
                key={f.key}
                n={source.filter((t) => matchesFilter(t, f.key)).length}
                label={f.label}
                alert={f.alert}
                active={filter === f.key}
                onClick={() =>
                  setFilter(filter === f.key && f.key !== DEFAULT_FILTER ? DEFAULT_FILTER : f.key)
                }
              />
            ))}
          </div>

          {canAssign && (
            <Button
              onClick={() => setAssignOpen(true)}
              className="btn-brand h-11 w-full sm:w-auto sm:self-start"
            >
              <Plus className="mr-1.5 size-4" />
              Assign task
            </Button>
          )}

          {/* What the list is showing, said in words. */}
          <div className="flex items-baseline justify-between">
            <span className="label-mono text-ink-faint">
              {filter === DEFAULT_FILTER
                ? scope === 'mine'
                  ? 'Still on your plate'
                  : 'Still owed by your people'
                : (FILTERS.find((f) => f.key === filter)?.label ?? 'Filtered')}
            </span>
            <span className="font-clock text-[12px] text-ink-faint">
              {visible.length} of {source.length}
              {filter !== DEFAULT_FILTER && (
                <button
                  type="button"
                  onClick={() => setFilter(DEFAULT_FILTER)}
                  className="ml-2 underline underline-offset-2 hover:text-ink"
                >
                  back to pending
                </button>
              )}
            </span>
          </div>

          {scope === 'mine' && filter === DEFAULT_FILTER && (
            <TaskProgress
              done={mine.filter((x) => x.status === 'DONE').length}
              total={mine.length}
            />
          )}

          {visible.length === 0 ? (
            <p className="rounded-[14px] border border-dashed border-rule px-5 py-10 text-center text-[13px] text-ink-faint">
              {source.length === 0
                ? scope === 'mine'
                  ? 'Nothing has been given to you.'
                  : 'Nobody below you has any work on.'
                : filter === 'PENDING'
                  ? 'Nothing outstanding — everything here is finished.'
                  : filter === 'DONE'
                    ? 'Nothing finished yet.'
                    : `Nothing is ${FILTERS.find((f) => f.key === filter)?.label ?? 'here'}.`}
            </p>
          ) : (
            <TaskGroups
              tasks={visible}
              own={scope === 'mine'}
              viewerId={user?.id}
              onMarkDone={(x) => void markDone(x as Task)}
              onStart={(x) => void startTask(x as Task)}
              onWithdraw={(x) => void withdraw(x as Task)}
              onReview={(x) => void openReview(x as Task)}
              onHistory={(x) => void openHistory(x as Task)}
            />
          )}

          {!canAssign && !receivesTasks && (
            <p className="text-[12.5px] leading-relaxed text-ink-faint">
              You have nobody below you, so there is nobody to assign work to.
            </p>
          )}
        </div>
      )}
      {/* Assigning — behind the button, not in front of the list. */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign a task</DialogTitle>
            <DialogDescription>
              Work flows downward. Miss the deadline and a flag is raised automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <FormField label="To" required>
              <Select
                value={form.assignedToId}
                onValueChange={(v) => setForm({ ...form, assignedToId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick someone" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} · {ROLE_LABEL[p.orgRole]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Task" required>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ship the geofence fix"
              />
            </FormField>

            <FormField label="Deadline" required>
              <Input
                type="datetime-local"
                value={form.dueAt}
                onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
              />
            </FormField>

            <FormField label="Details" helper="Optional">
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="resize-none"
              />
            </FormField>

            {/* PRIORITY — chips, as the design has them. */}
            <div>
              <div className="label-mono mb-2 text-ink-faint">Priority</div>
              <div className="flex gap-1.5">
                {(['LOW', 'NORMAL', 'URGENT'] as const).map((level) => {
                  const on = form.priority === level;
                  const urgent = level === 'URGENT';
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setForm({ ...form, priority: level })}
                      aria-pressed={on}
                      className="rounded-full px-3.5 py-1.5 text-[13px] capitalize transition-colors"
                      style={{
                        border: on
                          ? `2px solid ${urgent ? 'var(--dot-flag)' : 'var(--ink)'}`
                          : `1.5px solid ${urgent ? 'var(--dot-flag)' : 'var(--rule)'}`,
                        background: on ? (urgent ? 'var(--dot-flag)' : 'var(--ink)') : 'transparent',
                        color: on ? 'var(--paper)' : urgent ? 'var(--dot-flag)' : 'var(--ink-soft)',
                      }}
                    >
                      {level.toLowerCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="rounded-xl bg-[var(--state-warn-bg)] px-3.5 py-2.5 text-[11.5px] leading-relaxed text-ink">
              If this isn’t done by the deadline, a flag is raised automatically against{' '}
              {people.find((p) => p.id === form.assignedToId)?.name ?? 'them'}.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={busy || !form.title.trim() || !form.assignedToId}
              onClick={() => void assign()}
            >
              {busy ? 'Assigning…' : 'Assign task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Handing the work in: proof is the point ---------- */}
      <Dialog open={handIn !== null} onOpenChange={(open) => !open && setHandIn(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hand in “{handIn?.title}”</DialogTitle>
            <DialogDescription>
              Attach a screenshot or photo of the finished work. It goes to{' '}
              {handIn?.assignedBy.name ?? 'your reviewer'}, who accepts it or sends it back — the
              task is not done until they say so.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <FormField label="Proof" required>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => void pickProof(e.target.files?.[0])}
                  className="block w-full text-[12.5px] text-ink-soft file:mr-3 file:rounded-full file:border-0 file:bg-paper-sunk file:px-3 file:py-1.5 file:text-[12.5px] file:text-ink"
                />
                {proofImage && (
                  <div className="overflow-hidden rounded-[12px] border border-rule">
                    <img src={proofImage} alt="Proof of the finished work" className="w-full" />
                  </div>
                )}
                {proofImage && (
                  <p className="text-[11.5px] text-ink-faint">
                    {proofName} · resized for upload
                  </p>
                )}
              </div>
            </FormField>

            <FormField label="Anything to add?" helper="Optional">
              <Textarea
                value={proofNote}
                onChange={(e) => setProofNote(e.target.value)}
                rows={2}
                className="resize-none"
                placeholder="Deployed to staging — screenshot of the passing run."
              />
            </FormField>

            {/* Past the deadline, the reason is still required. */}
            {handIn && new Date(handIn.dueAt) < new Date() && (
              <FormField label="This is past its deadline — why?" required>
                <Textarea
                  value={delayReason}
                  onChange={(e) => setDelayReason(e.target.value)}
                  rows={2}
                  className="resize-none"
                />
              </FormField>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHandIn(null)} disabled={proofBusy}>
              Cancel
            </Button>
            <Button disabled={!proofImage || proofBusy} onClick={() => void submitHandIn()}>
              {proofBusy ? 'Sending…' : 'Hand in for review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- The whole trail: what happened, each time ---------- */}
      <Dialog open={historyTask !== null} onOpenChange={(open) => !open && setHistoryTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>“{historyTask?.title}” — what happened</DialogTitle>
            <DialogDescription>
              Every step, oldest first. The deadline never moved:{' '}
              {historyTask ? due(historyTask.dueAt) : ''}.
            </DialogDescription>
          </DialogHeader>

          {history === null ? (
            <Skeleton className="h-40 w-full rounded-[12px]" />
          ) : history.length === 0 ? (
            <p className="rounded-[12px] border border-dashed border-rule px-4 py-8 text-center text-[12.5px] text-ink-faint">
              Nothing was recorded for this task — it predates the trail.
            </p>
          ) : (
            <ol className="flex flex-col">
              {history.map((event, index) => (
                <li key={event.id} className="flex gap-3">
                  {/* The spine: a dot per step, joined by a line except at the end. */}
                  <div className="flex flex-none flex-col items-center">
                    <span
                      className="mt-1.5 size-2.5 rounded-full"
                      style={{ background: EVENT_TONE[event.kind] }}
                    />
                    {index < history.length - 1 && (
                      <span className="w-px flex-1 bg-rule" aria-hidden />
                    )}
                  </div>

                  <div className="min-w-0 flex-1 pb-4">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span
                        className="text-[13px] font-semibold"
                        style={{ color: EVENT_TONE[event.kind] }}
                      >
                        {EVENT_LABEL[event.kind]}
                      </span>
                      <span className="text-[11.5px] text-ink-faint">
                        {event.actor?.id === user?.id
                          ? 'by you'
                          : `by ${event.actor?.name ?? 'someone since removed'}`}
                        {event.actor && event.actor.id !== user?.id
                          ? ` · ${ROLE_LABEL[event.actor.orgRole]}`
                          : ''}
                      </span>
                      <span className="text-[11px] text-ink-ghost">{due(event.at)}</span>
                    </div>

                    {event.note && (
                      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-soft">
                        “{event.note}”
                      </p>
                    )}

                    {/* The screenshot from THIS attempt, fetched only on demand. */}
                    {event.hasProof && historyTask && (
                      openProof[event.id] ? (
                        <div className="mt-1.5 overflow-hidden rounded-[10px] border border-rule">
                          <img src={openProof[event.id]} alt={`Proof from ${EVENT_LABEL[event.kind]}`} className="w-full" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void loadStepProof(historyTask.id, event.id)}
                          className="mt-1 text-[11.5px] text-ink-faint underline underline-offset-2 hover:text-ink"
                        >
                          Show what was handed in{event.proofName ? ` (${event.proofName})` : ''}
                        </button>
                      )
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTask(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- The reviewer's decision ---------- */}
      <Dialog open={reviewing !== null} onOpenChange={(open) => !open && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review “{reviewing?.title}”</DialogTitle>
            <DialogDescription>
              Handed in by {reviewing?.assignedTo.name}. Sending it back returns it to them
              unfinished — the deadline does not move.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {reviewing?.proofNote && (
              <p className="rounded-xl bg-paper-sunk px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink-soft">
                “{reviewing.proofNote}”
              </p>
            )}

            {reviewProof ? (
              <div className="overflow-hidden rounded-[12px] border border-rule">
                <img src={reviewProof} alt="Proof of the finished work" className="w-full" />
              </div>
            ) : (
              <Skeleton className="h-48 w-full rounded-[12px]" />
            )}

            <FormField
              label="If you are sending it back, what needs fixing?"
              helper="Required to send back"
            >
              <Textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
                className="resize-none"
                placeholder="The screenshot shows the old build — re-run after deploying."
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="text-flag"
              disabled={reviewBusy}
              onClick={() => void decideReview('REJECT')}
            >
              Send back
            </Button>
            <Button disabled={reviewBusy} onClick={() => void decideReview('ACCEPT')}>
              {reviewBusy ? 'Saving…' : 'Accept — mark done'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closing after the deadline — say why */}
      <Dialog open={lateTask !== null} onOpenChange={(open) => !open && setLateTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>This task is past its deadline</DialogTitle>
            <DialogDescription>
              {lateTask && (
                <>
                  “{lateTask.title}” was due {due(lateTask.dueAt)}. Say what delayed it — your
                  CTO or CEO can waive the flag if the reason is fair.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <FormField label="Why was it delayed?" required>
            <Textarea
              value={delayReason}
              onChange={(e) => setDelayReason(e.target.value)}
              rows={4}
              placeholder="Blocked on the staging database until Thursday."
              className="resize-none"
            />
          </FormField>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLateTask(null)}>
              Cancel
            </Button>
            <Button
              disabled={!delayReason.trim()}
              onClick={() => lateTask && void closeAsSupervisor(lateTask, delayReason.trim())}
            >
              Submit and close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

export default function TasksRoute() {
  return (
    <AuthGuard>
      <TasksPage />
    </AuthGuard>
  );
}
